/**
 * Indian-numbering "amount in words" converter for invoices.
 *
 * Renders e.g.  ₹ 1,52,300.50  → "Rupees One Lakh Fifty Two Thousand Three Hundred and Fifty Paise Only"
 *
 * Strictly follows the Indian lakh/crore convention used on every printed
 * Indian tax invoice — auditors and GST officers expect this exact phrasing.
 * Handles values up to 99 crore (sufficient for SME invoices).
 *
 * Edge cases handled:
 *   • Zero            → "Rupees Zero Only"
 *   • No paise        → "Rupees ... Only" (no "and Paise" clause)
 *   • Pure paise      → "Paise ... Only"
 *   • Negative        → "Minus ..." prefix (used for credit notes)
 *   • Rounds to 2 dp before conversion so floating-point noise never leaks.
 */
const ONES = [
  "", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine",
  "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen",
  "Seventeen", "Eighteen", "Nineteen",
];
const TENS = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];

function _twoDigits(n) {
  if (n < 20) return ONES[n];
  const t = Math.floor(n / 10);
  const o = n % 10;
  return o === 0 ? TENS[t] : `${TENS[t]} ${ONES[o]}`;
}

function _threeDigits(n) {
  if (n < 100) return _twoDigits(n);
  const h = Math.floor(n / 100);
  const r = n % 100;
  return r === 0 ? `${ONES[h]} Hundred` : `${ONES[h]} Hundred ${_twoDigits(r)}`;
}

/** Convert a non-negative integer (≤ 99,99,99,999) to Indian-words. */
function _intToWords(n) {
  if (n === 0) return "Zero";
  const parts = [];
  // Crore (10^7) — capped at 99 crore.
  const crore = Math.floor(n / 10_000_000);
  if (crore > 0) parts.push(`${_twoDigits(crore)} Crore`);
  n %= 10_000_000;
  // Lakh (10^5)
  const lakh = Math.floor(n / 100_000);
  if (lakh > 0) parts.push(`${_twoDigits(lakh)} Lakh`);
  n %= 100_000;
  // Thousand
  const thousand = Math.floor(n / 1000);
  if (thousand > 0) parts.push(`${_twoDigits(thousand)} Thousand`);
  n %= 1000;
  // Hundreds + remainder
  if (n > 0) parts.push(_threeDigits(n));
  return parts.join(" ");
}

export function amountInWords(value) {
  const sign = value < 0 ? "Minus " : "";
  const abs = Math.abs(Number(value) || 0);
  // Round to 2dp BEFORE splitting — avoids floating-point artefacts like 0.295 → 0.29.
  const rounded = Math.round(abs * 100) / 100;
  const rupees = Math.floor(rounded);
  const paise = Math.round((rounded - rupees) * 100);

  if (rupees === 0 && paise === 0) return `${sign}Rupees Zero Only`;
  if (rupees === 0) return `${sign}Paise ${_twoDigits(paise)} Only`;

  const ruWords = _intToWords(rupees);
  if (paise === 0) return `${sign}Rupees ${ruWords} Only`;
  return `${sign}Rupees ${ruWords} and Paise ${_twoDigits(paise)} Only`;
}
